import { HttpContextToken, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { from, catchError, switchMap, throwError } from 'rxjs';
import { Routes } from '../enums/routes.enum';
import { AuthService } from '../services/auth.service';
import { LogService } from '../services/log.service';

const AUTH_RETRY_CONTEXT = new HttpContextToken<boolean>(() => false);

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
	const log = inject(LogService);
	const router = inject(Router);
	const auth = inject(Auth);
	const authService = inject(AuthService);
	return next(req).pipe(
		catchError(err => {
			if (err.status !== 401) {
				return throwError(() => err);
			}

			// Check if we're on an auth route that should not redirect
			const currentUrl = router.url;
			const authRoutes = [
				Routes.Login,
				Routes.SignUp,
				Routes.SignUpPassword,
				Routes.SignUpEmail,
				Routes.ForgotPassword,
				Routes.PasswordReset,
				Routes.Logout,
				Routes.Onboard
			];
			const isOnAuthRoute = authRoutes.some(route => currentUrl.startsWith(route));
			if (isOnAuthRoute) {
				log.debug('401 error on auth route, not redirecting');
				return throwError(() => err);
			}

			// Retry exactly once with a force-refreshed Firebase token.
			const hasRetried = req.context.get(AUTH_RETRY_CONTEXT);
			if (!hasRetried && auth.currentUser) {
				return from(auth.currentUser.getIdToken(true)).pipe(
					switchMap(refreshedToken => {
						authService.setToken(refreshedToken);
						log.debug('401 received, refreshed Firebase token and retrying request', { url: req.url });
						return next(
							req.clone({
								context: req.context.set(AUTH_RETRY_CONTEXT, true),
								setHeaders: {
									Authorization: `Bearer ${refreshedToken}`
								}
							})
						);
					}),
					catchError(refreshError => {
						log.debug('Token refresh failed after 401, routing to login', refreshError);
						void auth.signOut();
						void router.navigate([Routes.Login], {
							queryParams: { reason: 'session-expired' },
							replaceUrl: true
						});
						return throwError(() => err);
					})
				);
			}

			// No current user or already retried and still unauthorized.
			log.debug('Unauthorized after retry, routing to login');
			void auth.signOut();
			void router.navigate([Routes.Login], {
				queryParams: { reason: 'session-expired' },
				replaceUrl: true
			});
			return throwError(() => err);
		})
	);
};
